import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoginForm } from '@/features/auth/components/LoginForm';
import { login, loginTotp } from '@/lib/auth';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { fetchCurrentUser } from '@/lib/auth';
import { mapApiError } from '@/lib/error-map';
import type { LoginRequest } from '@formacion-docente/schemas';
import { TotpStep } from '@/features/auth/components/TotpStep';

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') ?? '/';
  const setUser = useAuthStore((s) => s.setUser);
  const setStatus = useAuthStore((s) => s.setStatus);
  const setTotpRequired = useAuthStore((s) => s.setTotpRequired);
  const status = useAuthStore((s) => s.status);
  const tempToken = useAuthStore((s) => s.tempToken);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function handleLogin(values: LoginRequest) {
    setLoading(true);
    setError(undefined);
    try {
      const outcome = await login(values.email, values.password);
      if (outcome.kind === 'totp_required') {
        setTotpRequired(outcome.tempToken);
        return;
      }
      // Login OK → hidratar store con /auth/me (trae grupos)
      setStatus('loading');
      const me = await fetchCurrentUser();
      setUser(me);
      navigate(redirect, { replace: true });
    } catch (e) {
      setError(mapApiError(e));
    } finally {
      setLoading(false);
    }
  }

  async function handleTotp(code: string) {
    if (!tempToken) return;
    setLoading(true);
    setError(undefined);
    try {
      const me = await loginTotp(tempToken, code);
      setUser(me);
      navigate(redirect, { replace: true });
    } catch (e) {
      setError(mapApiError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-2 text-center">
          <CardTitle className="text-2xl">Panel de Formación Docente</CardTitle>
          <p className="text-sm text-text-secondary">Inicia sesión para continuar</p>
        </CardHeader>
        <CardContent>
          {status === 'totp_required' && tempToken ? (
            <TotpStep onSubmit={handleTotp} onBack={() => setStatus('idle')} loading={loading} error={error} />
          ) : (
            <LoginForm onSubmit={handleLogin} loading={loading} errorMessage={error} />
          )}
          <div className="mt-4 text-center text-sm">
            <a href="/forgot-password" className="text-primary hover:underline">
              ¿Olvidó su contraseña?
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}